import { forwardRef, type ReactNode } from "react";

export function getInputClassName(isValid: boolean, extra?: string) {
  return [
    "form-field-input w-full h-10 font-onest text-[15px] leading-snug",
    "rounded-lg border px-3 py-2",
    "transition-[border-color,box-shadow,background-color] duration-200",
    "placeholder:text-slate-400",
    "focus:outline-none focus:ring-2 focus:ring-offset-0",
    isValid
      ? "border-slate-200 bg-white text-slate-900 shadow-sm shadow-slate-900/5 hover:border-slate-300 focus:border-brand-500 focus:ring-brand-500/15"
      : "form-field-input--invalid border-red-300 bg-red-50/40 text-slate-900 focus:border-red-400 focus:ring-red-400/20",
    extra,
  ]
    .filter(Boolean)
    .join(" ");
}

export function getTriggerClassName(isValid: boolean, hasValue: boolean) {
  return [
    getInputClassName(isValid),
    "inline-flex items-center justify-between gap-2 text-left",
    hasValue ? "text-slate-900" : "text-slate-400",
  ].join(" ");
}

export function FieldLabel({
  children,
  htmlFor,
  muted = false,
}: {
  children: ReactNode;
  htmlFor?: string;
  muted?: boolean;
}) {
  const empty =
    children == null ||
    children === false ||
    (typeof children === "string" && children.trim() === "");
  if (empty) return null;

  return (
    <div className="flex min-h-8 shrink-0 items-end">
      <label
        htmlFor={htmlFor}
        className={[
          "form-field-label line-clamp-3 block text-sm font-medium leading-snug font-onest",
          muted ? "text-slate-400" : "text-slate-700",
        ].join(" ")}
      >
        {children}
      </label>
    </div>
  );
}

export function FieldControl({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={`min-h-10 shrink-0 ${className}`.trim()}>{children}</div>
  );
}

export const FieldShell = forwardRef<
  HTMLDivElement,
  { children: ReactNode; className?: string }
>(({ children, className = "" }, ref) => (
  <div
    ref={ref}
    className={`flex w-full flex-col scroll-mt-24 sm:scroll-mt-8 ${className}`.trim()}
  >
    {children}
  </div>
));

FieldShell.displayName = "FieldShell";

export function FieldFooter({ children }: { children?: ReactNode }) {
  return (
    <div className="mt-0.5 min-h-0 text-xs leading-snug font-onest empty:hidden">
      {children}
    </div>
  );
}

export function FieldHint({ children }: { children: ReactNode }) {
  return (
    <FieldFooter>
      <span className="text-slate-500">{children}</span>
    </FieldFooter>
  );
}

export function FieldError({ children }: { children: ReactNode }) {
  return (
    <FieldFooter>
      <span className="form-field-error text-red-600" role="alert">
        {children}
      </span>
    </FieldFooter>
  );
}
